import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { _js } from '@ifixit/localize';
import { fireGlobal, goToLastPage } from 'Shared/utils';
import { authenticateWithCode } from '../services/authService';
import { withLoadingIndicator } from '../utils/loadingUtils';
import type { AuthResponse } from '../types';

interface UseLoginCodeProps {
   email: string;
   onNavigateBack: () => void;
}

/**
 * Validates that the code is exactly 6 digits
 */
const validateLoginCode = (code: string): string => {
   if (code.length !== 6) {
      return _js('Please enter a 6-digit code.');
   }
   return '';
};

/**
 * Maps authentication error responses to user-friendly messages
 */
const getErrorMessage = (response: AuthResponse): string => {
   const messages = (window as any).App?.loginMessages;

   switch (response.reason) {
      case 'email': {
         return messages?.invalid_email || _js('Invalid email address.');
      }
      case 'password': {
         return messages?.invalid_code || _js('Invalid verification code.');
      }
      case 'tooManyCodeAttempts': {
         return (
            messages?.tooManyCodeAttempts || _js('Too many code attempts. Please try again later.')
         );
      }
      default: {
         return response.message || response.reason || _js('Login failed. Please try again.');
      }
   }
};

/**
 * Handles successful login by firing events and redirecting
 */
const handleLoginSuccess = (response: AuthResponse) => {
   fireGlobal('login.success', response);
   goToLastPage();
};

/**
 * Handles failed login by firing events and returning error message
 */
const handleLoginFailure = (response: AuthResponse): string => {
   fireGlobal('login.failure', response);
   return getErrorMessage(response);
};

export const useLoginCode = ({ email, onNavigateBack }: UseLoginCodeProps) => {
   const [code, setCode] = useState('');
   const [error, setError] = useState<string>('');

   const loginMutation = useMutation({
      mutationFn: ({ email, code }: { email: string; code: string }) =>
         authenticateWithCode(email, code),
      onSuccess: handleLoginSuccess,
      onError: (response: AuthResponse) => {
         setError(handleLoginFailure(response));
         setCode(''); // Clear code on failure
      },
   });

   const clearError = useCallback(() => {
      setError('');
   }, []);

   const submitCode = useCallback(async () => {
      const validationError = validateLoginCode(code);
      if (validationError) {
         setError(validationError);
         return;
      }

      setError('');

      try {
         await withLoadingIndicator(loginMutation.mutateAsync({ email, code }), {
            pendingMessage: _js('Verifying code...'),
            successMessage: _js('Login successful'),
            failureMessage: _js('Code verification failed'),
         });
      } catch (error) {
         console.error('Error during login:', error);
         // Error is already handled in onError callback
      }
   }, [code, email, loginMutation]);

   return {
      code,
      setCode,
      isSubmitting: loginMutation.isPending,
      error,
      clearError,
      submitCode,
      navigateBack: onNavigateBack,
   };
};
